/**
 * @file Builds the CSV export of the transfer table, with the source and target tier columns.
 */
import { useMemo } from "react";
import { useOutletContext } from "react-router-dom";

import { formatName } from "@components/Export";
import { useExportAction } from "@components/Table";
import { PROFILE_TYPES_LABELS } from "@components/Domain";

import {
  getCurrencyValue,
  getInvestmentTierAmount,
  getInvestmentTierCurrency,
} from "./getInvestedAmount";
import type { Investment, OutletContext, TransferInvestment } from "../types";

const FILE_NAME = "transfer_investments";

export type ExportInvestment = Pick<
  TransferInvestment,
  "id" | "name" | "investor" | "profileType" | "investments" | "sourceInvestmentId"
>;

type SourceInvestment = Pick<Investment, "id" | "investments">;

type Column = {
  title: string;
  value: (investment: ExportInvestment) => string | null | undefined;
};

type Options = {
  getItems: () => ExportInvestment[];
  sourceProjectId: string;
  targetProjectId?: string;
  sourceInvestments: SourceInvestment[];
};

/**
 * Returns a function that exports the transfer table's items to a CSV file with the source and target tier amounts.
 */
const useExport = ({
  getItems,
  sourceProjectId,
  targetProjectId,
  sourceInvestments,
}: Options): () => void => {
  const { getProject, organization } = useOutletContext<OutletContext>();
  const sourceProject = getProject(sourceProjectId);
  const targetProject = getProject(targetProjectId);

  /**
   * Finds the source investment with the given id.
   */
  const getSourceInvestment = (investmentId: string) =>
    sourceInvestments.find(({ id }) => id === investmentId);

  const sourceProjectTierIds = useMemo(() =>
    sourceProject
      ? sourceProject.tiers.map(({ name }) => name)
      : []
  , [ sourceProject ]);

  const targetProjectTierIds = useMemo(() =>
    targetProject
      ? targetProject.tiers.map(({ name }) => name)
      : []
  , [ targetProject ]);

  const sourceTierColumns = sourceProjectTierIds
    .map((tierId): Column => ({
      title: `Source ${tierId}`,
      /**
       * Returns the source amount for this tier, from the linked source investment or the row itself.
       */
      value: (investment) => {
        const { sourceInvestmentId } = investment;

        if (sourceInvestmentId) {
          const sourceInvestment = getSourceInvestment(sourceInvestmentId);

          return getInvestmentTierCurrency(tierId, sourceInvestment);
        }

        const amount = getInvestmentTierAmount(tierId, investment);

        if (!amount) {
          return "";
        }

        return getInvestmentTierCurrency(tierId, investment);
      },
    }));

  const targetTierColumns = targetProjectTierIds
    .map((tierId): Column => ({
      title: `Target ${tierId}`,
      /**
       * Returns the target amount for this tier, combined with the linked source investment's amount.
       */
      value: (investment) => {
        const { sourceInvestmentId } = investment;

        if (!sourceInvestmentId) {
          return getInvestmentTierCurrency(tierId, investment);
        }

        const sourceInvestment = getSourceInvestment(sourceInvestmentId);

        const targetAmount = getInvestmentTierAmount(tierId, investment) || 0;
        const sourceAmount = getInvestmentTierAmount(tierId, sourceInvestment) || 0;

        const sumAmount = targetAmount + sourceAmount;

        if (sumAmount) {
          return getCurrencyValue(sumAmount);
        }

        return "";
      },
    }));

  const columns: Column[] = [
    {
      title: "Organization ID",
      /**
       * Returns the organization id.
       */
      value: () => organization.id,
    },
    {
      title: "Source Project ID",
      /**
       * Returns the source project id.
       */
      value: () => sourceProjectId,
    },
    {
      title: "Source Project Name",
      /**
       * Returns the source project name.
       */
      value: () => getProject(sourceProjectId)?.name,
    },
    {
      title: "Target Project ID",
      /**
       * Returns the target project id.
       */
      value: () => targetProjectId,
    },
    {
      title: "Target Project Name",
      /**
       * Returns the target project name.
       */
      value: () => getProject(targetProjectId)?.name,
    },
    {
      title: "Source Investment ID",
      /**
       * Returns the source investment id, whether the row is a source investment or an override target.
       */
      value: ({ id, sourceInvestmentId }) => sourceInvestmentId || id,
    },
    {
      title: "Target Investment ID",
      /**
       * Returns the target investment id when the row overrides a target investment.
       */
      value: ({ id, sourceInvestmentId }) => (sourceInvestmentId ? id : ""),
    },
    {
      title: "Investment Profile Name",
      /**
       * Returns the trimmed investment profile name.
       */
      value: ({ name }) => name.trim(),
    },
    {
      title: "Investor Legal Full Name",
      /**
       * Returns the investor's full legal name built from its given, additional and family names.
       */
      value: ({ investor }) => {
        const { givenName, familyName, additionalName } = investor;

        return formatName([ givenName, additionalName, familyName ].join(" "));
      },
    },
    {
      title: "Investment Profile Type",
      /**
       * Returns the label for the investment's profile type.
       */
      value: ({ profileType }) => PROFILE_TYPES_LABELS[profileType] || "",
    },
    ...sourceTierColumns,
    ...targetTierColumns,
  ];

  const onExport = useExportAction(getItems, FILE_NAME, columns);

  return onExport;
};

export default useExport;
